import { useContext, useState } from 'react'
import { ChevronRight } from 'lucide-react'
import {
  ActionDependant,
  GlobalDataContext,
  TemplateContext,
  evaluateTemplateValue,
} from '@ea-lab/reactive-json'

/**
 * Generic, self-contained JSON viewer bound to a store value.
 *
 *  - object / non-empty array → a collapsible, syntax-coloured tree (each node
 *    expands on click) ;
 *  - scalar (string / number)  → a wrapped <pre> block ;
 *  - null / '' / empty         → the `emptyLabel` ("Vide").
 *
 * Optional behaviours:
 *  - `title`  → renders a clickable disclosure header; the body stays hidden
 *    until the title is clicked (so empty/large payloads don't clutter the UI).
 *  - `highlight` → a search string (usually bound to a store path shared by a
 *    search field): every key/value containing it is <mark>-highlighted, and
 *    the path to each match auto-expands — including the section disclosure.
 *  - `defaultExpandDepth` → tree depth opened by default once visible (1).
 *
 * YAML usage:
 *   - type: JsonTreeView
 *     title: 'En-têtes de la réponse'
 *     data: ~.detail.resHeaders
 *     highlight: ~~.detailQuery
 */

const isEmptyVal = (v) => {
  if (v == null || v === '') return true
  if (Array.isArray(v)) return v.length === 0
  if (typeof v === 'object') return Object.keys(v).length === 0
  return false
}

// Does this value (deep) contain the query in any key or scalar leaf?
const matchesDeep = (value, q) => {
  if (!q) return false
  if (value == null) return 'null'.includes(q)
  if (typeof value !== 'object') return String(value).toLowerCase().includes(q)
  if (Array.isArray(value)) return value.some((v) => matchesDeep(v, q))
  return Object.entries(value).some(([k, v]) => k.toLowerCase().includes(q) || matchesDeep(v, q))
}

function Highlight({ text, q }) {
  const s = String(text)
  if (!q) return s
  const lower = s.toLowerCase()
  if (!lower.includes(q)) return s
  const out = []
  let i = 0
  let key = 0
  let pos = lower.indexOf(q)
  while (pos !== -1) {
    if (pos > i) out.push(s.slice(i, pos))
    out.push(
      <mark key={key++} className="bg-yellow-200 text-gray-900 rounded px-0.5">{s.slice(pos, pos + q.length)}</mark>,
    )
    i = pos + q.length
    pos = lower.indexOf(q, i)
  }
  if (i < s.length) out.push(s.slice(i))
  return out
}

const scalarClass = (v) => {
  if (v === null) return 'text-rose-500'
  if (typeof v === 'string') return 'text-emerald-700 break-all'
  if (typeof v === 'number') return 'text-indigo-600'
  if (typeof v === 'boolean') return 'text-amber-600 font-medium'
  return 'text-gray-600'
}

function Scalar({ value, q }) {
  if (typeof value === 'string') {
    return <span className={scalarClass(value)}>"<Highlight text={value} q={q} />"</span>
  }
  return <span className={scalarClass(value)}>{value === null ? 'null' : <Highlight text={String(value)} q={q} />}</span>
}

function Row({ label, value, q, depth, defaultDepth }) {
  const isObj = value !== null && typeof value === 'object'
  const pad = { paddingLeft: `${8 + depth * 14}px` }
  const labelEl =
    label != null ? (
      <span className="text-sky-800 font-semibold">
        <Highlight text={label} q={q} />
        <span className="text-gray-400 font-normal">: </span>
      </span>
    ) : null

  // Hooks must run unconditionally — declare manual state before any return.
  const [manual, setManual] = useState(depth < defaultDepth)

  if (!isObj) {
    return (
      <div style={pad} className="leading-6 font-mono text-[13px]">
        {labelEl}
        <Scalar value={value} q={q} />
      </div>
    )
  }

  const entries = Array.isArray(value) ? value.map((v, i) => [String(i), v]) : Object.entries(value)
  // A search hit forces the path open; otherwise honour the manual toggle.
  const forced = q ? matchesDeep(value, q) : null
  const isOpen = forced != null ? forced : manual
  const [openBrace, closeBrace] = Array.isArray(value) ? ['[', ']'] : ['{', '}']

  return (
    <div className="font-mono text-[13px]">
      <div
        style={pad}
        className="leading-6 cursor-pointer select-none rounded hover:bg-gray-100/70"
        onClick={() => { if (forced == null) setManual((m) => !m) }}
      >
        <ChevronRight className={`inline w-3 h-3 -mt-0.5 text-gray-400 transition-transform ${isOpen ? 'rotate-90' : ''}`} />
        {labelEl}
        <span className="text-gray-400">{openBrace}{isOpen ? '' : `… ${closeBrace}`}</span>
        {!isOpen && <span className="ml-1 text-gray-300">{entries.length}</span>}
      </div>
      {isOpen && (
        <div>
          {entries.map(([k, v]) => (
            <Row key={k} label={k} value={v} q={q} depth={depth + 1} defaultDepth={defaultDepth} />
          ))}
          <div style={pad} className="leading-6 text-gray-400">{closeBrace}</div>
        </div>
      )}
    </div>
  )
}

export const JsonTreeView = ({ props }) => {
  const globalDataContext = useContext(GlobalDataContext)
  const templateContext = useContext(TemplateContext)
  const resolve = (v) => evaluateTemplateValue({ valueToEvaluate: v, globalDataContext, templateContext })

  const data = resolve(props.data)
  const title = resolve(props.title)
  const emptyLabel = resolve(props.emptyLabel) ?? 'Vide'
  const rawHighlight = resolve(props.highlight)
  const q = typeof rawHighlight === 'string' ? rawHighlight.trim().toLowerCase() : ''
  const parsedDepth = Number(resolve(props.defaultExpandDepth))
  const defaultExpandDepth = Number.isFinite(parsedDepth) ? parsedDepth : 1

  const empty = isEmptyVal(data)
  const hasMatch = q ? matchesDeep(data, q) : false

  // Section disclosure (only when a `title` is given): collapsed by default; a
  // search hit forces it open so matches reveal themselves automatically.
  const [open, setOpen] = useState(false)
  const sectionOpen = title == null ? true : open || hasMatch

  const body = empty ? (
    <p className="px-2 py-1 text-sm italic text-gray-400">{emptyLabel}</p>
  ) : typeof data === 'object' ? (
    <div className="max-h-96 overflow-auto rounded-lg border border-gray-200 bg-slate-50 p-2">
      <Row value={data} q={q} depth={0} defaultDepth={defaultExpandDepth} />
    </div>
  ) : (
    <pre className="max-h-80 overflow-auto whitespace-pre-wrap break-all rounded border border-gray-100 bg-gray-50 p-3 text-xs text-gray-700">
      <Highlight text={String(data)} q={q} />
    </pre>
  )

  return (
    <ActionDependant {...props}>
      {title != null ? (
        <div>
          <button
            type="button"
            onClick={() => setOpen((o) => !o)}
            className="flex w-full items-center gap-1.5 text-left text-xs font-semibold uppercase tracking-wider text-gray-500 hover:text-gray-700"
          >
            <ChevronRight className={`w-3.5 h-3.5 transition-transform ${sectionOpen ? 'rotate-90' : ''}`} />
            {title}
            {empty && <span className="ml-1 font-normal normal-case text-gray-300">(vide)</span>}
            {q && hasMatch && <span className="ml-1 inline-block h-1.5 w-1.5 rounded-full bg-yellow-400" />}
          </button>
          {sectionOpen && <div className="mt-2">{body}</div>}
        </div>
      ) : (
        body
      )}
    </ActionDependant>
  )
}
